import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import SvgIcon from 'components/atoms/SvgIcon'
import I18n from '../../../../core/i18n'
import { agentService, utilService } from '../../../../core/services'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { CheckBox, InputText } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import AgentLayoutNew from '../_layouts/new'

declare var window: interfaces.IWindow

const FIELDS = {
  name: 'name',
  slug: 'slug',
  login_id: 'login_id',
  password: 'password',
  is_active: 'is_active',
  is_guest_list_active: 'is_guest_list_active',
}

const PASSWORD_LENGTH = 16
const PASSWORD_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

const generatePassword = () =>
  Array.from(crypto.getRandomValues(new Uint32Array(PASSWORD_LENGTH)))
    .map(n => PASSWORD_CHARS[n % PASSWORD_CHARS.length])
    .join('')

const selectCustomStyles = {
  control: (provided, _) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

const title = I18n.t('admin.create_new', {
  model: I18n.t('partner_venue', { scope: 'activerecord.models' }),
})

interface IProps {
  selectable_posts: interfaces.IPartnerVenuePost[]
  user: any
}

const AgentPartnerVenueNew: React.FC<IProps> = ({ selectable_posts: selectablePosts, user }) => {
  const { data: userData } = utilService.getDataFromJson(user)
  const [selectedPosts, setSelectedPosts] = React.useState([])
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)
  const [initialPassword] = React.useState(generatePassword)

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const postIds = (selectedPosts || []).map(post => Number(post.value))
      if (postIds.length === 0) {
        window.flashMessages.addMessage({
          text: '紐づけるツアーを1件以上選択してください',
          type: 'error',
        })
        return
      }
      try {
        const { flush } = await agentService.createPartnerVenue({
          name: values.name,
          slug: values.slug,
          login_id: values.login_id,
          is_active: values.is_active,
          is_guest_list_active: values.is_guest_list_active,
          password: values.password,
          post_ids: postIds,
        })
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
        location.href = '/agent/partner_venues'
      } catch (e) {
        // エラー時のフラッシュはhttpClientのインターセプターで表示済み
      }
    },
    [selectedPosts]
  )

  return (
    <SidebarLayout user={userData} showSidebar={true}>
      <AgentLayoutNew
        model="partner_venues"
        title={title}
        fields={FIELDS}
        hideAgentNav={true}
        indexLink="/agent/partner_venues"
        formItems={
          <>
            <S.FormItemWrapper>
              <InputText
                required={true}
                name="name"
                defaultValue=""
                label={I18n.t('activerecord.attributes.partner_venue.name')}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={true}
                name="login_id"
                defaultValue=""
                label={I18n.t('activerecord.attributes.partner_venue.login_id')}
                placeholder="例: hotel-abc-login または hotel@example.com"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={true}
                name="slug"
                defaultValue=""
                label={I18n.t('activerecord.attributes.partner_venue.slug')}
                placeholder="例: hotel-abc (半角英小文字・数字・ハイフン)"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.ReactSelectWrapper>
                <p className="ReactSelect_Title">
                  {I18n.t('activerecord.attributes.partner_venue.posts')}
                  <span className="InputText_Required">{I18n.t('generic.required')}</span>
                </p>
                <div className="ReactSelect_Main">
                  <ReactSelect
                    placeholder="選択してください"
                    options={selectablePosts.map(post => ({
                      value: String(post.id),
                      label: post.short_name,
                    }))}
                    isMulti={true}
                    onChange={setSelectedPosts}
                    styles={selectCustomStyles}
                    menuPortalTarget={document.querySelector('body')}
                  />
                </div>
              </S.ReactSelectWrapper>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <CheckBox name="is_active" defaultChecked={true} label="公開" />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <CheckBox
                name="is_guest_list_active"
                defaultChecked={false}
                label="ゲスト名簿を公開"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.PasswordWrapper>
                <InputText
                  required={true}
                  password={!isPasswordVisible}
                  name="password"
                  defaultValue={initialPassword}
                  label={I18n.t('activerecord.attributes.partner_venue.password')}
                />
                <button
                  type="button"
                  className="toggleVisibility"
                  aria-label="パスワードの表示切替"
                  onClick={() => setIsPasswordVisible(visible => !visible)}
                >
                  <SvgIcon
                    name={isPasswordVisible ? 'visibility_off' : 'visibility_filled'}
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                </button>
              </S.PasswordWrapper>
            </S.FormItemWrapper>
          </>
        }
        handleSubmit={handleSubmit}
      />
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.PasswordWrapper = styled.div`
  display: flex;
  align-items: center;

  .InputText {
    flex: 1;
  }

  .toggleVisibility {
    flex: none;
    margin-left: 8px;
    padding: 4px;
    border: none;
    background: none;
    cursor: pointer;
    color: ${constants.COLORS.subText};

    .icon {
      width: 20px;
      height: 20px;
    }

    &:hover {
      color: ${constants.COLORS.black};
    }
  }
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 180px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    .InputText_Required {
      display: inline-block;
      border-radius: 3px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.caution};
      vertical-align: middle;
      font-size: 11px;
      font-weight: normal;
      padding: 4px 6px;
      margin-left: 8px;
    }

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

export default AgentPartnerVenueNew
